(() => {
  const HANDOFF_PARAM = "cart_handoff";
  const TRANSFER_ID_PARAM = "cart_transfer_id";
  const STORAGE_PREFIX = "market-cart-handoff:";
  const ACTIVE_TRANSFER_KEY = "market-cart-handoff:active";
  const MAX_LINES = 50;
  const MAX_QUANTITY = 1000;
  const MAX_HANDOFF_LENGTH = 1500;
  const MAX_URL_LENGTH = 2000;
  const PROCESSING_STALE_AFTER = 30_000;
  const FINAL_CART_READ_ATTEMPTS = 3;
  const FINAL_CART_READ_DELAY = 150;
  const CART_DRAWER_REFRESH_TIMEOUT = 3_000;
  const SHOP_SWITCH_LINK_SELECTOR =
    "#modal-shop-switch a.CountriesList__item, #shop-switch .shop-switch__country a, a.country-selector__link";

  let isSwitchingMarket = false;
  let activeDestinationTransferId = null;
  let hasRequestedFallbackReload = false;

  const hasOwnKeys = (value) => value && typeof value === "object" && Object.keys(value).length > 0;
  const hasLineProperties = (value) =>
    value != null && (typeof value !== "object" || Object.keys(value).length > 0);

  const isEligibleLine = (item) => {
    const variantId = Number(item?.variant_id);
    const quantity = Number(item?.quantity);

    return (
      Number.isSafeInteger(variantId) &&
      variantId > 0 &&
      Number.isSafeInteger(quantity) &&
      quantity > 0 &&
      quantity <= MAX_QUANTITY &&
      !hasLineProperties(item.properties) &&
      !item.selling_plan_allocation &&
      !item.selling_plan &&
      !item.selling_plan_id &&
      !item.gift_card &&
      !item.remote &&
      !hasOwnKeys(item.components) &&
      !hasOwnKeys(item.item_components) &&
      !item.parent_relationship &&
      !item.parent &&
      !item.bundle
    );
  };

  const createPayload = (items) => {
    const quantities = new Map();

    for (const item of items) {
      if (!isEligibleLine(item)) continue;

      const variantId = Number(item.variant_id);
      const quantity = (quantities.get(variantId) || 0) + Number(item.quantity);

      if (quantity > MAX_QUANTITY) continue;
      quantities.set(variantId, quantity);
    }

    if (quantities.size === 0 || quantities.size > MAX_LINES) return null;

    const payload = `v1.${Array.from(quantities, ([variantId, quantity]) => `${variantId}:${quantity}`).join(",")}`;
    return payload.length <= MAX_HANDOFF_LENGTH ? payload : null;
  };

  const parsePayload = (payload) => {
    if (typeof payload !== "string" || !payload.startsWith("v1.")) return null;
    if (payload.length > MAX_HANDOFF_LENGTH) return null;

    const lines = payload.slice(3).split(",");
    if (lines.length === 0 || lines.length > MAX_LINES) return null;

    const seenVariantIds = new Set();
    const parsedLines = [];

    for (const line of lines) {
      const match = line.match(/^([1-9]\d{0,15}):([1-9]\d{0,3})$/);
      if (!match) return null;

      const variantId = Number(match[1]);
      const quantity = Number(match[2]);

      if (
        !Number.isSafeInteger(variantId) ||
        !Number.isSafeInteger(quantity) ||
        quantity > MAX_QUANTITY ||
        seenVariantIds.has(variantId)
      ) {
        return null;
      }

      seenVariantIds.add(variantId);
      parsedLines.push({ variantId, quantity });
    }

    return parsedLines;
  };

  const isTransferId = (value) =>
    typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const getTransferState = (transferId) => {
    try {
      const state = JSON.parse(sessionStorage.getItem(`${STORAGE_PREFIX}${transferId}`));
      return state && typeof state === "object" ? state : null;
    } catch {
      return null;
    }
  };

  const hasValidLines = (lines) =>
    Array.isArray(lines) &&
    lines.length > 0 &&
    lines.length <= MAX_LINES &&
    lines.every(
      (line, index) =>
        Number.isSafeInteger(line?.variantId) &&
        line.variantId > 0 &&
        Number.isSafeInteger(line?.quantity) &&
        line.quantity > 0 &&
        line.quantity <= MAX_QUANTITY &&
        lines.findIndex((other) => other.variantId === line.variantId) === index
    );

  const getActiveTransferId = () => {
    try {
      const transferId = sessionStorage.getItem(ACTIVE_TRANSFER_KEY);
      return isTransferId(transferId) ? transferId : null;
    } catch {
      return null;
    }
  };

  const setTransferState = (transferId, status, lines) => {
    try {
      const previousState = getTransferState(transferId);
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${transferId}`,
        JSON.stringify({
          status,
          updatedAt: Date.now(),
          lines: lines || previousState?.lines,
        })
      );

      if (status === "processing") {
        sessionStorage.setItem(ACTIVE_TRANSFER_KEY, transferId);
      } else if (sessionStorage.getItem(ACTIVE_TRANSFER_KEY) === transferId) {
        sessionStorage.removeItem(ACTIVE_TRANSFER_KEY);
      }

      return true;
    } catch {
      return false;
    }
  };

  const cleanHandoffParams = (url) => {
    url.searchParams.delete(HANDOFF_PARAM);
    url.searchParams.delete(TRANSFER_ID_PARAM);
    history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const fetchCart = async () => {
    const response = await fetch(`${Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error("Unable to fetch cart");
    return response.json();
  };

  const addLine = async ({ variantId, quantity }) => {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ id: variantId, quantity }),
    });

    return response.ok;
  };

  const refreshThemeCart = () => {
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  };

  const verifyFinalCart = async (expectedItemCount) => {
    for (let attempt = 0; attempt < FINAL_CART_READ_ATTEMPTS; attempt++) {
      const cart = await fetchCart();
      if (cart.item_count >= expectedItemCount) return;

      if (attempt < FINAL_CART_READ_ATTEMPTS - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, FINAL_CART_READ_DELAY));
      }
    }

    throw new Error("Destination cart did not reach the expected item count");
  };

  const waitForCartDrawerRefresh = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return Promise.resolve(false);

    return new Promise((resolve) => {
      let timeout;
      const complete = (didRefresh) => {
        window.clearTimeout(timeout);
        document.removeEventListener("cart-drawer:refreshed", onRefresh);
        resolve(didRefresh);
      };
      const onRefresh = (event) => {
        if (event.target === cartDrawer) complete(true);
      };

      document.addEventListener("cart-drawer:refreshed", onRefresh);
      timeout = window.setTimeout(() => complete(false), CART_DRAWER_REFRESH_TIMEOUT);
    });
  };

  const isMainCartPage = () =>
    document.querySelector(".shopify-section--main-cart") ||
    window.location.pathname === new URL(`${Shopify.routes.root}cart`, window.location.origin).pathname;

  const reloadOnce = () => {
    if (hasRequestedFallbackReload) return;

    hasRequestedFallbackReload = true;
    window.location.reload();
  };

  const scheduleRetry = (transferId, delay = PROCESSING_STALE_AFTER) => {
    window.setTimeout(() => {
      const state = getTransferState(transferId);
      if (getActiveTransferId() !== transferId || state?.status !== "processing" || !hasValidLines(state.lines)) return;

      const elapsed = Date.now() - Number(state.updatedAt);
      if (elapsed < PROCESSING_STALE_AFTER) {
        scheduleRetry(transferId, PROCESSING_STALE_AFTER - elapsed);
        return;
      }

      beginDestinationTransfer(transferId, state.lines);
    }, delay);
  };

  const runDestinationTransfer = async (transferId, lines) => {
    try {
      const cart = await fetchCart();

      if (cart.item_count > 0) {
        refreshThemeCart();
        setTransferState(transferId, "skipped");
        return;
      }

      let successfulItemCount = 0;

      for (const line of lines) {
        try {
          if (await addLine(line)) successfulItemCount += line.quantity;
        } catch {
          // A single unavailable line must not block the remaining handoff lines.
        }
      }

      await verifyFinalCart(successfulItemCount);
      const drawerRefresh = waitForCartDrawerRefresh();
      refreshThemeCart();
      const didRefreshDrawer = await drawerRefresh;
      setTransferState(transferId, "completed");

      if (isMainCartPage() || !didRefreshDrawer) reloadOnce();
    } catch {
      // Keep the safe payload briefly so an interrupted request can retry.
      scheduleRetry(transferId);
    } finally {
      activeDestinationTransferId = null;
    }
  };

  const beginDestinationTransfer = (transferId, lines) => {
    if (activeDestinationTransferId) return;
    if (!setTransferState(transferId, "processing", lines)) return;

    activeDestinationTransferId = transferId;
    runDestinationTransfer(transferId, lines);
  };

  const resumePendingTransfer = () => {
    const transferId = getActiveTransferId();
    const state = transferId && getTransferState(transferId);

    if (state?.status !== "processing" || !hasValidLines(state.lines)) return;

    refreshThemeCart();

    const elapsed = Date.now() - Number(state.updatedAt);
    if (elapsed >= PROCESSING_STALE_AFTER) {
      beginDestinationTransfer(transferId, state.lines);
    } else {
      scheduleRetry(transferId, PROCESSING_STALE_AFTER - elapsed);
    }
  };

  const processDestinationHandoff = () => {
    const url = new URL(window.location.href);
    const payload = url.searchParams.get(HANDOFF_PARAM);
    const transferId = url.searchParams.get(TRANSFER_ID_PARAM);
    const lines = parsePayload(payload);

    if (!lines || !isTransferId(transferId)) {
      resumePendingTransfer();
      return;
    }

    const previousState = getTransferState(transferId);
    cleanHandoffParams(url);

    if (previousState?.status === "completed" || previousState?.status === "skipped") return;

    if (previousState?.status === "processing") {
      resumePendingTransfer();
      return;
    }

    beginDestinationTransfer(transferId, lines);
  };

  const handleShopSwitchClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const anchor = event.target.closest(SHOP_SWITCH_LINK_SELECTOR);
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const destinationUrl = new URL(anchor.href, window.location.href);

    if (destinationUrl.hostname === window.location.hostname) return;

    event.preventDefault();

    if (isSwitchingMarket) return;
    isSwitchingMarket = true;

    // The live Shop Switch URL retains its existing ?switch parameter, but never reuses handoff data.
    destinationUrl.searchParams.delete(HANDOFF_PARAM);
    destinationUrl.searchParams.delete(TRANSFER_ID_PARAM);

    try {
      const cart = await fetchCart();
      const payload = createPayload(cart.items || []);

      if (payload && window.crypto?.randomUUID) {
        destinationUrl.searchParams.set(HANDOFF_PARAM, payload);
        destinationUrl.searchParams.set(TRANSFER_ID_PARAM, window.crypto.randomUUID());

        if (destinationUrl.href.length <= MAX_URL_LENGTH) {
          window.location.assign(destinationUrl.href);
          return;
        }
      }
    } catch {
      // A cart-read failure must not block a Market switch.
    }

    window.location.assign(destinationUrl.href);
  };

  // The country selector links are rendered with the current path only (Liquid has no access to the query string),
  // so carry the current query (search, filters, variant…) over before the handoff below reads the link.
  const CARRIED_QUERY_IGNORED_PARAMS = ["switch", "_gl", HANDOFF_PARAM, TRANSFER_ID_PARAM];

  const carryQueryToCountrySelectorLink = (event) => {
    if (!(event.target instanceof Element)) return;

    const anchor = event.target.closest("a.country-selector__link");
    if (!anchor) return;

    const destinationUrl = new URL(anchor.href, window.location.href);

    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!CARRIED_QUERY_IGNORED_PARAMS.includes(key) && !destinationUrl.searchParams.has(key)) {
        destinationUrl.searchParams.append(key, value);
      }
    });

    anchor.href = destinationUrl.href;
  };

  window.addEventListener("click", carryQueryToCountrySelectorLink, true);
  document.addEventListener("click", handleShopSwitchClick, true);
  processDestinationHandoff();
})();
